/* Search 1.2.2: native facets and a compact, recoverable search experience. */
(() => {
  'use strict';
  const fold = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  const audienceKey = (param, label) => param === 'filter.p.product_type' && ['femei', 'barbati', 'unisex'].includes(fold(label)) ? fold(label) : null;
  function targetFromEntries(href, entries) {
    const target = new URL(href);
    for (const key of [...target.searchParams.keys()]) if (key.startsWith('filter.') || key === 'page' || key === 'iu_search_loaded') target.searchParams.delete(key);
    for (const [key, value] of entries) if (key.startsWith('filter.') && value !== '') target.searchParams.append(key, value);
    return target;
  }
  // Pure helpers exposed for regression tests; no fetches or catalog mutations.
  window.IUSearchFilterUI = {audienceKey, targetFromEntries};
  function initializeMobileSidebar(page) {
    const panel = page.querySelector('#halo-sidebar');
    if (!panel) return;
    const mobile = window.matchMedia('(max-width: 1024px)');
    const triggers = [...page.querySelectorAll('[data-sidebar]')];
    const closeButton = panel.querySelector('[data-close-sidebar]');
    let open = false, opener = null;
    const visible = element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    const focusables = () => [...panel.querySelectorAll('a[href],button,input:not([type="hidden"]),select,textarea,summary,[tabindex]')]
      .filter(element => !element.disabled && element.tabIndex >= 0 && !element.closest('[hidden],[inert]') && visible(element));
    // A native guide or another visible modal owns its own focus and Escape key.
    const otherDialog = () => [...document.querySelectorAll('dialog[open],[role="dialog"][aria-modal="true"]')]
      .some(element => element !== panel && element.getAttribute('aria-hidden') !== 'true' && visible(element));
    const focusPanel = () => {
      let attempts = 0;
      const focus = () => {
        if (!open || otherDialog()) return;
        const target = focusables()[0];
        (target || panel).focus({preventScroll:true});
        // Ella makes the panel visible during its opening transition.
        if (!panel.contains(document.activeElement) && ++attempts < 40) requestAnimationFrame(focus);
      };
      requestAnimationFrame(() => requestAnimationFrame(focus));
    };
    const close = () => {
      // Keep Ella's close event, overlay and scroll state as the source of truth.
      closeButton?.click();
      document.body.classList.remove('open-mobile-sidebar');
    };
    const sync = () => {
      const next = mobile.matches && document.body.classList.contains('open-mobile-sidebar');
      panel.inert = mobile.matches && !next;
      if (mobile.matches) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-hidden', String(!next));
      } else {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-hidden');
      }
      if (next) panel.setAttribute('aria-modal', 'true');
      else panel.removeAttribute('aria-modal');
      triggers.forEach(trigger => trigger.setAttribute('aria-expanded', String(next)));
      const previous = open;
      open = next;
      if (!previous && open) focusPanel();
      if (previous && !open && mobile.matches && !otherDialog() && opener?.isConnected && visible(opener)) opener.focus({preventScroll:true});
    };
    page.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const trigger = event.target.closest('[data-sidebar]');
      if (trigger && mobile.matches) opener = trigger;
    }, true);
    document.addEventListener('keydown', event => {
      if (!open || otherDialog()) return;
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation(); close(); return;
      }
      if (event.key !== 'Tab') return;
      const items = focusables(), first = items[0], last = items[items.length - 1];
      if (!first) {event.preventDefault(); panel.focus({preventScroll:true}); return;}
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }, true);
    document.addEventListener('focusin', event => {
      if (open && !otherDialog() && !panel.contains(event.target)) (focusables()[0] || panel).focus({preventScroll:true});
    });
    new MutationObserver(sync).observe(document.body, {attributes:true, attributeFilter:['class']});
    mobile.addEventListener('change', () => {
      if (!mobile.matches && document.body.classList.contains('open-mobile-sidebar')) close();
      sync();
    });
    sync();
  }
  function initialize() {
    const page = document.querySelector('.iu-search-page');
    if (!page || page.dataset.iuFiltersReady) return;
    page.dataset.iuFiltersReady = 'true';
    initializeMobileSidebar(page);
    const current = new URL(window.location.href);
    const withContext = target => window.IUStoreSearch ? window.IUStoreSearch.url(current.searchParams.get('iu_q') || current.searchParams.get('q') || '', target.href) : target;
    const navigate = target => window.location.assign(withContext(target).href);
    const queryInput = page.querySelector('.iu-search-form input[name="q"]');
    const clearQuery = page.querySelector('[data-iu-clear-query]');
    if (queryInput && clearQuery) {
      queryInput.value = current.searchParams.get('iu_q') || queryInput.value;
      const showClear = () => { clearQuery.hidden = !queryInput.value; };
      queryInput.addEventListener('input', showClear);
      clearQuery.addEventListener('click', () => { queryInput.value = ''; showClear(); queryInput.focus(); });
      showClear();
    }
    page.querySelectorAll('[data-iu-edit-search]').forEach(button => button.addEventListener('click', () => {
      if (!queryInput) return;
      queryInput.focus(); queryInput.select();
    }));
    page.querySelectorAll('[data-iu-reset-results]').forEach(link => {
      const target = new URL(current.href);
      let hasFilters = false;
      for (const key of [...target.searchParams.keys()]) if (key.startsWith('filter.')) { target.searchParams.delete(key); hasFilters = true; }
      target.searchParams.delete('page'); target.searchParams.delete('iu_search_loaded');
      link.hidden = !hasFilters;
      link.href = withContext(target).href;
      link.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); navigate(new URL(link.href, current));
      });
    });
    const filterCount = page.querySelector('[data-iu-filter-count]');
    if (filterCount) {
      const selected = new Set();
      for (const [key, value] of current.searchParams) {
        if (!key.startsWith('filter.') || !value) continue;
        const row = [...page.querySelectorAll('[data-iu-option]')].find(item => {
          const input = item.querySelector('input'); return input.name === key && input.value === value;
        });
        const audience = audienceKey(key, row ? row.dataset.label : value);
        selected.add(key.startsWith('filter.v.price.') ? 'price' : key + ':' + (audience || value));
      }
      filterCount.textContent = String(selected.size);
      filterCount.hidden = selected.size === 0;
    }
    const form = page.querySelector('[data-iu-filters]');
    const audienceValues = new Map();
    if (form) {
      // Shopify may mark only the grouped value active even when the URL contains
      // both audience values. Preserve the explicit selection on subsequent edits.
      form.querySelectorAll('input[type="checkbox"][name]').forEach(input => {
        if (current.searchParams.getAll(input.name).includes(input.value)) input.checked = true;
      });
      const holder = form.querySelector('.iu-filter-groups');
      [...holder.querySelectorAll('.iu-filter-group')].sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order)).forEach(group => holder.append(group));
      const proxies = new Map();
      const updateGroupCounts = () => {
        form.querySelectorAll('.iu-filter-group').forEach(group => {
          const badge = group.querySelector('[data-iu-group-count]');
          if (!badge) return;
          const selected = [...group.querySelectorAll('[data-iu-option]')].filter(row => !row.dataset.iuOriginal && row.querySelector('input').checked).length;
          badge.textContent = String(selected); badge.hidden = selected === 0;
        });
      };
      // Merge only duplicate audience labels. Never merge arbitrary brands or sizes.
      form.querySelectorAll('.iu-filter-group').forEach(group => {
        const members = new Map();
        group.querySelectorAll('[data-iu-option]').forEach(row => {
          const input = row.querySelector('input');
          const key = audienceKey(input.name, row.dataset.label);
          if (!key) return;
          if (!audienceValues.has(key)) audienceValues.set(key, []);
          audienceValues.get(key).push(input.value);
          if (!members.has(key)) members.set(key, []);
          members.get(key).push(row);
        });
        members.forEach(rows => {
          if (rows.length < 2) return;
          const originals = rows.map(row => row.querySelector('input'));
          const representative = rows[0].cloneNode(true);
          const proxy = representative.querySelector('input');
          proxy.removeAttribute('name');
          proxy.checked = originals.some(input => input.checked);
          proxy.dataset.iuProxy = 'true';
          const count = rows.reduce((sum, row) => sum + Number(row.dataset.count), 0);
          representative.querySelector('.iu-filter-count').textContent = String(count);
          representative.querySelector('.iu-filter-count').setAttribute('aria-label', count + ' produse');
          rows[0].before(representative);
          rows.forEach(row => {row.hidden = true; row.dataset.iuOriginal = 'true';});
          proxies.set(proxy, originals);
        });
        const rows = [...group.querySelectorAll('[data-iu-option]')].filter(row => !row.dataset.iuOriginal);
        const more = group.querySelector('[data-iu-show-more]');
        const search = group.querySelector('[data-iu-brand-search]');
        let expanded = false;
        function display() {
          const term = search ? fold(search.value) : '';
          let matches = 0;
          rows.forEach((row, index) => {
            const matched = !term || fold(row.dataset.label).includes(term);
            if (matched) matches++;
            row.hidden = !matched || (!term && !expanded && index >= 8 && !row.querySelector('input').checked);
          });
          if (more) {more.hidden = rows.length <= 8 || Boolean(term); more.textContent = expanded ? 'Vezi mai puține' : 'Vezi toate (' + rows.length + ')'; more.setAttribute('aria-expanded', String(expanded));}
          const empty = group.querySelector('[data-iu-brand-empty]');
          if (empty) empty.hidden = matches !== 0;
        }
        if (search) {search.closest('[data-iu-brand-search-wrapper]').hidden = false; search.addEventListener('input', display); search.addEventListener('keydown', event => {if(event.key === 'Enter') event.preventDefault();});}
        if (more) more.addEventListener('click', () => {expanded = !expanded; display();});
        display();
        // Keep sizes within easy reach on a phone; selected categories stay open.
        if (group.dataset.order === '20' && window.matchMedia('(max-width: 1024px)').matches && !rows.some(row => row.querySelector('input').checked)) group.removeAttribute('open');
      });
      const mobile = () => window.matchMedia('(max-width: 1024px)').matches;
      function submit() {
        const min = form.querySelector('.iu-filter-prices input:first-of-type');
        const priceInputs = [...form.querySelectorAll('.iu-filter-prices input')];
        if (priceInputs.length === 2 && priceInputs.every(input => input.value !== '') && Number(priceInputs[0].value) > Number(priceInputs[1].value)) {
          form.querySelector('[data-iu-filter-status]').textContent = 'Prețul minim trebuie să fie mai mic decât prețul maxim.';
          min.focus(); return;
        }
        navigate(targetFromEntries(current.href, new FormData(form)));
      }
      form.addEventListener('change', event => {
        if (event.target.matches('[data-iu-brand-search]')) return;
        if (proxies.has(event.target)) proxies.get(event.target).forEach(input => {input.checked = event.target.checked;});
        if (!event.target.matches('input[type="checkbox"]')) return;
        updateGroupCounts();
        if (mobile()) form.querySelector('[data-iu-filter-status]').textContent = 'Apasă „Aplică filtrele” pentru a actualiza produsele.';
        else submit();
      });
      form.addEventListener('submit', event => {event.preventDefault(); if (form.reportValidity()) submit();});
      updateGroupCounts();
    }
    const collections = page.querySelector('[data-iu-collections]');
    if (collections) {
      collections.querySelectorAll('[data-iu-collection]').forEach(link => {
        const target = new URL(current.href);
        target.searchParams.delete('page'); target.searchParams.delete('iu_search_loaded');
        target.searchParams.delete(collections.dataset.param);
        if (link.dataset.iuCollection) target.searchParams.set(collections.dataset.param, link.dataset.iuCollection);
        link.href = withContext(target).href;
      });
      collections.addEventListener('click', event => {
        const link = event.target.closest('a[data-iu-collection]');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); navigate(new URL(link.href, current));
      });
    }
    const applied = page.querySelector('[data-iu-applied]');
    if (applied) {
      const groups = new Map();
      applied.querySelectorAll('[data-iu-remove-filter]').forEach(link => {
        const key = audienceKey(link.dataset.param, link.dataset.label);
        if (!key) return;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(link);
      });
      groups.forEach(links => links.slice(1).forEach(link => {link.hidden = true;}));
      // Real hrefs also support keyboard and modified clicks on grouped chips.
      applied.querySelectorAll('a').forEach(link => {
        const target = new URL(current.href); target.searchParams.delete('page'); target.searchParams.delete('iu_search_loaded');
        if (link.hasAttribute('data-iu-clear-filters')) {
          for (const key of [...target.searchParams.keys()]) if (key.startsWith('filter.')) target.searchParams.delete(key);
        } else if (link.hasAttribute('data-iu-remove-price')) {
          target.searchParams.delete(link.dataset.min); target.searchParams.delete(link.dataset.max);
        } else {
          const key = audienceKey(link.dataset.param, link.dataset.label);
          const remove = key ? [...(audienceValues.get(key) || []), ...groups.get(key).map(item => item.dataset.value)] : [link.dataset.value];
          const keep = target.searchParams.getAll(link.dataset.param).filter(value => !remove.includes(value) && (!key || audienceKey(link.dataset.param, value) !== key));
          target.searchParams.delete(link.dataset.param); keep.forEach(value => target.searchParams.append(link.dataset.param, value));
        }
        link.href = withContext(target).href;
      });
      applied.addEventListener('click', event => {
        const link = event.target.closest('a');
        if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); navigate(new URL(link.href, current));
      });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once:true});
  else initialize();
})();
